import { useRef, type ComponentProps } from "react";
import { AccountPlayerCard } from "@softmax/product-ux/account";
import type { PlayerResponse } from "@softmax/observatory-client/types";
import {
  usePlayerMutation,
  type PlayerAction,
} from "@/features/players/commands";
import type { CredentialOpening } from "./credential-intent";
import { isUncertainWrite } from "../write-failure";
import { CredentialsRegion } from "./credential-region";

export function PlayerCredentialCard({
  player,
  onCredential,
  onRename,
  refreshing,
  refreshError,
  onRefresh,
}: {
  player: PlayerResponse;
  onCredential(opening: CredentialOpening): void;
  onRename(): void;
  refreshing: boolean;
  refreshError: string | undefined;
  onRefresh(): Promise<boolean>;
}) {
  const mutation = usePlayerMutation({ elevatedPrivileges: false });
  const submitted = useRef(false);
  const uncertain = !!mutation.error && isUncertainWrite(mutation.error);
  const blocked = mutation.isPending || uncertain;
  const submit = (action: PlayerAction) => {
    if (submitted.current || blocked) return;

    submitted.current = true;
    mutation.mutate(action, {
      onSettled: () => {
        submitted.current = false;
      },
    });
  };
  let feedback: Pick<
    ComponentProps<typeof AccountPlayerCard>,
    "error" | "recovery"
  > = {};

  if (uncertain) {
    feedback = { ...feedback, recovery: { refreshing } };

    if (refreshError)
      feedback = {
        ...feedback,
        recovery: { refreshing, message: `Refresh failed: ${refreshError}` },
      };
  }

  if (mutation.error)
    feedback = {
      ...feedback,
      error: uncertain
        ? `The update result is unknown. Refresh players before retrying. ${mutation.error.message}`
        : mutation.error.message,
    };

  return (
    <AccountPlayerCard
      player={{
        id: player.id,
        name: player.name,
        isDefault: player.is_default,
        avatarUrl: player.avatar_url ?? null,
      }}
      pending={mutation.isPending}
      {...feedback}
      onRefresh={async () => {
        if (await onRefresh()) mutation.reset();
      }}
      credentials={
        <CredentialsRegion
          target={{
            kind: "player",
            playerId: player.id,
            playerName: player.name,
          }}
          onOpen={onCredential}
        />
      }
      onRename={() => {
        if (!blocked) onRename();
      }}
      onMakeDefault={() => {
        submit({ kind: "default", player });
      }}
      onRemoveAvatar={() => {
        submit({ kind: "remove-avatar", player });
      }}
      onUploadAvatar={(file) => {
        submit({ kind: "upload-avatar", player, file });
      }}
    />
  );
}
